import { c as _c } from "react/compiler-runtime";
import { Button, Tooltip, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material';
import type { Condition, Provider } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';
import { ScrollableContent } from '@/components/scroll/scrollableContent';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
import { Loader } from '@/components/shared/loader';
import { useDAL } from '@/contexts/useDAL';
import { findValueCondition, PropertyType, type ConditionPanelHost, type FilterProperty } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { tagEntityLabel, tagOptionLabel, type TagFilterMode } from './tagKeyValueUtils';
import { TagSearchBoxes } from './tagSearchBoxes';
import { useTagOptions } from './useTagOptions';
import { MultiSelectFilter, type FilterProps } from '../filters';
import { TAG_KEYS_PROPERTY, TAG_KEY_VALUES_PROPERTY } from '../propertyNames';

/**
 * TagsFilter is dedicated to the resource-tags filter, so it owns the mapping
 * between the selected filter mode and the property the condition is written
 * against: key-only selections must target `tagKeys` and key=value selections
 * `tagKeyValues`, otherwise the backend filters on the wrong column (EON-12659).
 * The mapping lives here (rather than importing the property objects from
 * properties.tsx) because properties.tsx imports this module — referencing it
 * back would create a circular dependency.
 */
const TAG_FILTER_PROPERTY_BY_MODE: Record<'key' | 'key-value', FilterProperty> = {
  key: {
    name: TAG_KEYS_PROPERTY,
    displayName: 'Tag keys',
    type: PropertyType.MultipleValues,
    group: 'primary'
  },
  'key-value': {
    name: TAG_KEY_VALUES_PROPERTY,
    displayName: 'Tag key-value pairs',
    type: PropertyType.MultipleValues,
    group: 'primary'
  }
};
export interface TagsFilterProps extends FilterProps {
  supportedProviders?: Provider[];
  onDoneClick: () => void;
  initialFilterMode: TagFilterMode;
  panelHost?: ConditionPanelHost;
  /** Runs whenever the popover closes, hosted or not — for the wrapper's own cleanup. */
  onPanelClose?: () => void;
  dataSource?: 'tags' | 'sourceAccountTags';
}
export interface TagsDataResult {
  data: {
    tags?: {
      key: string;
      value?: string | null;
    }[];
  } | undefined;
  isLoading: boolean;
}
export function useResourceTagsData(filter?: Condition): TagsDataResult {
  const dal = useDAL();
  return dal.inventory.tags.useList(filter);
}
export function useSourceAccountTagsData(filter?: Condition): TagsDataResult {
  const dal = useDAL();
  return dal.inventory.sourceAccountTags.useList(filter);
}
const ResourceTagsFilterWrapper = props => {
  const $ = _c(3);
  const tagsData = useResourceTagsData();
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsFilterContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
const SourceAccountTagsFilterWrapper = props => {
  const $ = _c(3);
  const tagsData = useSourceAccountTagsData();
  let t0;
  if ($[0] !== props || $[1] !== tagsData) {
    t0 = <TagsFilterContent {...props} tagsData={tagsData} />;
    $[0] = props;
    $[1] = tagsData;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const TagsFilter = props => {
  const $ = _c(4);
  if (props.dataSource === "sourceAccountTags") {
    let t0;
    if ($[0] !== props) {
      t0 = <SourceAccountTagsFilterWrapper {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props) {
    t0 = <ResourceTagsFilterWrapper {...props} />;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
const TagsFilterContent = props => {
  const $ = _c(53);
  const [keyFilter, setKeyFilter] = useState("");
  const [valueFilter, setValueFilter] = useState("");
  const [filterMode, setFilterMode] = useState(props.initialFilterMode);
  const {
    data: body,
    isLoading
  } = props.tagsData;
  const activeProperty = TAG_FILTER_PROPERTY_BY_MODE[filterMode === "key" ? "key" : "key-value"];
  let t0;
  if ($[0] !== activeProperty.name || $[1] !== props.conditions) {
    t0 = findValueCondition(props.conditions, activeProperty.name);
    $[0] = activeProperty.name;
    $[1] = props.conditions;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const condition = t0;
  let t1;
  if ($[3] !== condition?.value) {
    t1 = condition?.value ?? [];
    $[3] = condition?.value;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const selectedResources = t1;
  const t2 = body?.tags;
  let t3;
  if ($[5] !== filterMode || $[6] !== keyFilter || $[7] !== selectedResources || $[8] !== t2 || $[9] !== valueFilter) {
    t3 = {
      tags: t2,
      selectedValues: selectedResources,
      keyFilter,
      valueFilter,
      mode: filterMode
    };
    $[5] = filterMode;
    $[6] = keyFilter;
    $[7] = selectedResources;
    $[8] = t2;
    $[9] = valueFilter;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const options = useTagOptions(t3);
  if (isLoading) {
    let t4;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Loader />;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    return t4;
  }
  let t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      gap: "12px"
    };
    t5 = <Typography>Filter by</Typography>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = event => setFilterMode(event.target.value);
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <MenuItem value="key">Tag keys</MenuItem>;
    t8 = <MenuItem value="key-value">Tag key-value pairs</MenuItem>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  let t9;
  if ($[17] !== filterMode) {
    t9 = <Select value={filterMode} onChange={t6} data-testid="tags-filter-mode">{t7}{t8}</Select>;
    $[17] = filterMode;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== filterMode) {
    t10 = pluralize(tagOptionLabel(filterMode));
    $[19] = filterMode;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = `To filter your resources, select one or more ${t10}.`;
  let t12;
  if ($[21] !== t11) {
    t12 = <Typography className="my-[12px]" variant="body1">{t11}</Typography>;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== filterMode || $[24] !== keyFilter || $[25] !== valueFilter) {
    t13 = <TagSearchBoxes mode={filterMode} keyFilter={keyFilter} valueFilter={valueFilter} onKeyFilterChange={setKeyFilter} onValueFilterChange={setValueFilter} />;
    $[23] = filterMode;
    $[24] = keyFilter;
    $[25] = valueFilter;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t12 || $[28] !== t13 || $[29] !== t9) {
    t14 = <ScrollableHeader className="px-[24px] pb-[24px] py-[12px]" sx={t4}>{t5}{t9}{t12}{t13}</ScrollableHeader>;
    $[27] = t12;
    $[28] = t13;
    $[29] = t9;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== options.length) {
    t15 = options.length === 0 && <Typography variant="body1" className="my-[12px]">{"No matching values found. Try a new search term."}</Typography>;
    $[31] = options.length;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== activeProperty || $[34] !== keyFilter || $[35] !== options || $[36] !== props || $[37] !== valueFilter) {
    t16 = options.length > 0 && <MultiSelectFilter {...props} property={activeProperty} data-testid={`filter-${activeProperty.name}`} showSelectAll={!!keyFilter || !!valueFilter} options={options} />;
    $[33] = activeProperty;
    $[34] = keyFilter;
    $[35] = options;
    $[36] = props;
    $[37] = valueFilter;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t15 || $[40] !== t16) {
    t17 = <ScrollableContent className="px-[24px] py-[12px]">{t15}{t16}</ScrollableContent>;
    $[39] = t15;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t14 || $[43] !== t17) {
    t18 = <ScrollableWrapper>{t14}{t17}</ScrollableWrapper>;
    $[42] = t14;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== condition || $[46] !== filterMode || $[47] !== props || $[48] !== selectedResources.length) {
    t19 = selectedResources.length > 0 && <Stack direction="row" className="px-[24px] py-[16px]" sx={{
      alignItems: "center",
      justifyContent: "space-between",
      boxShadow: "var(--mui-customShadows-panelUp)"
    }}><Typography>{`${selectedResources.length} ${pluralize(tagEntityLabel(filterMode), selectedResources.length)} selected`}</Typography><Stack direction="row" sx={{
        alignItems: "center"
      }}><Tooltip title="Clear all"><IconButton color="primary" onClick={() => props.onChange(props.conditions.filter(x => x !== condition))}><i className="material-symbols-filter-alt-off-outline-rounded h-[20px] w-[20px]" /></IconButton></Tooltip><Button className="ml-[12px]" variant="outlined" onClick={() => props.onDoneClick()}>Done</Button></Stack></Stack>;
    $[45] = condition;
    $[46] = filterMode;
    $[47] = props;
    $[48] = selectedResources.length;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t18 || $[51] !== t19) {
    t20 = <Stack className="h-full overflow-hidden">{t18}{t19}</Stack>;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  return t20;
};